"use client";

import React, { useState } from 'react';
import { ThumbsUp, MessageCircle, Trash2, Send, CheckCircle } from 'lucide-react';
import StarRating from './StarRating';
import { formatTimeAgo } from '../stories/utils/videoUtils';
import { useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import { toast } from 'sonner';
import { getOrCreateGuestId, getGuestAvatar, getGuestName } from '../stories/utils/videoUtils';

interface ReviewComment {
  _id: Id<"reviewComments">;
  userName: string;
  userAvatar: string;
  content: string;
  createdAt: number;
  isAdmin?: boolean;
  isBrandReply?: boolean;
}

interface Review {
  _id: Id<"productReviews">;
  userName: string;
  userAvatar: string;
  rating: number;
  title: string;
  content: string;
  createdAt: number;
  helpfulVotes: number;
  images?: string[];
  comments: ReviewComment[];
}

interface ReviewCardProps {
  review: Review;
  currentUserId?: string;
  onVote: (id: Id<"productReviews">) => void;
  onDelete?: (id: Id<"productReviews">) => void;
}

/** First letter of a shop or shopper name, for an avatar with no picture. */
function brandInitial(name?: string): string {
  const first = (name || "").trim().charAt(0);
  return first ? first.toUpperCase() : "?";
}

const ReviewCard: React.FC<ReviewCardProps> = ({ 
  review, 
  currentUserId,
  onVote, 
  onDelete 
}) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const addComment = useMutation(api.reviews.mutations.addReviewComment);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Get or create guest ID
      const guestId = getOrCreateGuestId();
      const guestName = getGuestName(guestId);
      const guestAvatar = getGuestAvatar(guestId);
      
      await addComment({
        reviewId: review._id,
        content: commentText.trim(),
        guestId,
        userName: guestName,
        userAvatar: guestAvatar,
      });
      
      setCommentText('');
      toast.success('Comment added!');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 mb-4 transition-shadow hover:shadow-md">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <img 
            src={review.userAvatar} 
            alt={review.userName} 
            className="w-10 h-10 rounded-full object-cover border border-slate-200" 
          />
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-slate-900">{review.userName}</h4>
              <CheckCircle className="w-3.5 h-3.5 text-blue-500" fill="currentColor" color="white" />
            </div>
            <div className="flex items-center gap-2">
              <StarRating rating={review.rating} size={14} readonly />
              <span className="text-xs text-slate-400">• {formatTimeAgo(new Date(review.createdAt).toISOString())}</span>
            </div>
          </div>
        </div>
        
        {/* Delete button for demo (admin only in production) */}
        {onDelete && currentUserId === 'admin' && (
          <button 
            onClick={() => onDelete(review._id)}
            className="text-slate-400 hover:text-red-500 transition-colors p-2"
            title="Delete Review"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="mb-4">
        <h3 className="font-bold text-slate-800 mb-1">{review.title}</h3>
        <p className="text-slate-600 text-sm leading-relaxed">{review.content}</p>
        
        {/* Images */}
        {review.images && review.images.length > 0 && (
          <div className="flex gap-2 mt-3">
            {review.images.map((img, idx) => (
              <img 
                key={idx} 
                src={img} 
                alt={`Review attachment ${idx}`} 
                className="w-16 h-16 rounded-lg object-cover border border-slate-200 cursor-pointer hover:opacity-90" 
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex items-center gap-4 pt-4 border-t border-slate-50">
        <button 
          onClick={() => onVote(review._id)}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
        >
          <ThumbsUp className="w-4 h-4" />
          <span>Helpful ({review.helpfulVotes})</span>
        </button>
        
        <button 
          onClick={() => setShowComments(!showComments)}
          className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${showComments ? 'text-blue-600' : 'text-slate-500 hover:text-slate-800'}`}
        >
          <MessageCircle className="w-4 h-4" />
          <span>{review.comments.length} Comments</span>
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="mt-4 bg-slate-50 rounded-xl p-4 animate-in fade-in slide-in-from-top-2 duration-200">
          
          {/* Existing Comments */}
          {review.comments.length > 0 ? (
            <div className="space-y-4 mb-4">
              {review.comments.map(comment => (
                <div key={comment._id} className="flex gap-3">
                  {comment.userAvatar ? (
                    <img
                      src={comment.userAvatar}
                      alt={comment.userName}
                      className="w-8 h-8 rounded-full object-cover shrink-0"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                    />
                  ) : (
                    <div
                      className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold ${comment.isBrandReply ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'}`}
                      aria-hidden
                    >
                      {brandInitial(comment.userName)}
                    </div>
                  )}
                  <div className="flex-1">
                     <div className="bg-white p-3 rounded-tr-xl rounded-br-xl rounded-bl-xl shadow-sm border border-slate-100">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-bold ${comment.isBrandReply ? 'text-blue-600' : 'text-slate-900'}`}>
                            {comment.userName}
                          </span>
                          {comment.isBrandReply && (
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600/70">
                              Store
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400">{formatTimeAgo(new Date(comment.createdAt).toISOString())}</span>
                        </div>
                        <p className="text-xs text-slate-700">{comment.content}</p>
                     </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-xs text-slate-400">
              No comments yet.
            </div>
          )}

          {/* Add Comment */}
          <form onSubmit={handleSubmitComment} className="relative">
            <input
              type="text"
              placeholder="Write a comment..."
              className="w-full pl-4 pr-10 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-blue-500 outline-none"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              disabled={isSubmitting}
            />
            <button 
              type="submit"
              disabled={!commentText.trim() || isSubmitting}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-blue-600 hover:bg-blue-50 rounded-full disabled:opacity-30 disabled:hover:bg-transparent transition-all"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ReviewCard;
